import React from 'react';
import AuthImage from '../AuthImage';
import {TripItem, TripItemStatus, TRIP_ITEM_STATUS_LABELS} from '../../service/TripApi';

interface Props {
    items: TripItem[];
    packedPercent: number;
    packedCount: number;
    totalItems: number;
    onStatusChange: (item: TripItem, status: TripItemStatus) => void;
    busyId?: string | null;
}

/**
 * Список вещей поездки с отметкой упаковки.
 *
 * <p>Чекбокс отвечает ровно за «в чемодане / нет», а «не беру» — отдельная кнопка:
 * смешивать отказ от вещи с неупакованностью нельзя, иначе прогресс сборов никогда не
 * дойдёт до 100 %.</p>
 */
const TripItemPackingList: React.FC<Props> = ({
    items, packedPercent, packedCount, totalItems, onStatusChange, busyId,
}) => (
    <div className="space-y-3">
        <div className="space-y-1">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                <span data-testid="trip-packing-progress">{packedPercent}% упаковано</span>
                <span>{packedCount} из {totalItems}</span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
                 role="progressbar" aria-valuenow={packedPercent} aria-valuemin={0} aria-valuemax={100}
                 aria-label="Прогресс упаковки">
                {/* Заполнение — scaleX, а не width: композитное свойство не вызывает лейаут. */}
                <div className="h-full w-full origin-left bg-brand-500 transition-transform duration-ui ease-out motion-reduce:transition-none"
                     style={{transform: `scaleX(${packedPercent / 100})`}}/>
            </div>
        </div>

        <ul className="grid gap-2" data-testid="trip-item-list">
            {items.map(item => {
                const packed = item.status === 'PACKED';
                const skipped = item.status === 'SKIPPED';
                return (
                    <li key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 ${skipped ? 'opacity-60' : ''}`}>
                        <input
                            type="checkbox"
                            checked={packed}
                            disabled={busyId === item.id}
                            onChange={() => onStatusChange(item, packed ? 'TO_PACK' : 'PACKED')}
                            data-testid={`trip-item-pack-${item.id}`}
                            aria-label={`Упаковано: ${item.name ?? 'вещь'}`}
                            className="w-5 h-5 min-w-[20px] accent-brand-500"
                        />
                        <span className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                            {item.primaryPhotoUrl ? (
                                <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name ?? ''}
                                           variant="thumbnail" className="w-full h-full object-cover"/>
                            ) : (
                                <span className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</span>
                            )}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {TRIP_ITEM_STATUS_LABELS[item.status]} · дней в плане: {item.daysUsed}
                            </span>
                        </span>
                        <button
                            type="button"
                            onClick={() => onStatusChange(item, skipped ? 'TO_PACK' : 'SKIPPED')}
                            disabled={busyId === item.id}
                            data-testid={`trip-item-skip-${item.id}`}
                            className="text-xs min-h-[44px] px-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                        >
                            {skipped ? 'Вернуть' : 'Не беру'}
                        </button>
                    </li>
                );
            })}
        </ul>
    </div>
);

export default TripItemPackingList;
